import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useMemo } from "react";

import { useDebounceEffect } from "../useDebounceEffect";

export const ElementVisibilityContext = React.createContext({});

/**
 * ElementVisibilityProvider provides mechanisms to register an element to be watched
 * by an intersection observer to detect when the element is visible on the viewport.
 * When any registered element is visible, a debounced callback will be triggered.
 */
export const ElementVisibilityProvider = (props) => {
  const { children, debounceDelay = 1000, onVisible, rememberVisible = true } = props;
  const [observer, setObserver] = useState(null);
  const [registeredElements, setRegisteredElements] = useState([]);
  const visibleElementsCount = useMemo(getVisibleElementsCount, [registeredElements]);

  useEffect(() => {
    const observerInstance = new IntersectionObserver(handleObservation, { threshold: 0.5 });

    setObserver(observerInstance);

    return () => {
      observerInstance?.disconnect();
    };
  }, []);

  useDebounceEffect(handleVisibleElementsChange, [visibleElementsCount], debounceDelay);

  return (
    <ElementVisibilityContext.Provider value={getContextValue()}>
      {children}
    </ElementVisibilityContext.Provider>
  );

  function getContextValue() {
    return {
      registerElement,
    };
  }

  function handleObservation(entries) {
    setRegisteredElements((previouslyRegisteredElements) => {
      const updatedElements = [...previouslyRegisteredElements];

      entries.forEach((entry) => {
        const { target, isIntersecting } = entry;
        const elementIndex = updatedElements.findIndex((item) => item.element === target);

        if (elementIndex !== -1) {
          const elementVisibility =
            isIntersecting || (rememberVisible && updatedElements[elementIndex].visible);

          updatedElements[elementIndex].visible = elementVisibility;
        }
      });

      return updatedElements;
    });
  }

  function handleVisibleElementsChange() {
    if (registeredElements.length > 0) {
      onVisible?.(registeredElements);
    }
  }

  function getVisibleElementsCount() {
    return registeredElements.filter((element) => element.visible).length;
  }

  function registerElement(element, elementMetadata) {
    if (element && observer && !registeredElements.some((item) => item.element === element)) {
      observer.observe(element);
      setRegisteredElements((elements) => [
        ...elements,
        { element, metadata: elementMetadata, visible: false },
      ]);
    }
  }
};

ElementVisibilityProvider.displayName = "ElementVisibilityProvider";

ElementVisibilityProvider.propTypes = {
  /** Content that will be rendered. */
  children: PropTypes.node.isRequired,

  /** Time in milliseconds to delay triggering onVisible and collect visibility. */
  debounceDelay: PropTypes.number,

  /**
   * Callback that is triggered when elements become visible. Callback parameter
   * will be an array of objects, containing information on all registered elements.
   */
  onVisible: PropTypes.func.isRequired,

  /**
   * Keep track of elements that become visible for the onVisible callback.
   */
  rememberVisible: PropTypes.bool,
};
